import { Suspense, lazy, useEffect, useState } from "react";
import { Search as SearchIcon } from "lucide-react";

// Chargé à la demande : cmdk + fuse.js ne pèsent plus sur le bundle initial.
const SearchDialog = lazy(() => import("@/components/SearchDialog"));

const OPEN_EVENT = "mg:open-search";

/** Ouvre la recherche depuis n'importe où (bouton de navigation, lien, etc.). */
export function openSearch() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

function isTextInputActive(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return el.isContentEditable;
}

export function Search() {
  const [open, setOpen] = useState(false);
  // Une fois ouverte, la boîte de dialogue reste montée (l'index est conservé).
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        if (isTextInputActive() && !open) return;
        e.preventDefault();
        setOpen((v) => !v);
        setMounted(true);
      }
    };
    const onOpen = () => {
      setOpen(true);
      setMounted(true);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, [open]);

  if (!mounted) return null;

  return (
    <Suspense fallback={null}>
      <SearchDialog open={open} onOpenChange={setOpen} />
    </Suspense>
  );
}

/** Bouton d'ouverture de la recherche, utilisable dans les barres de navigation. */
export function SearchButton({ className = "" }: { className?: string }) {
  return (
    <button
      type="button"
      onClick={openSearch}
      aria-label="Rechercher sur le site"
      title="Rechercher (⌘K)"
      className={`flex h-9 w-9 items-center justify-center rounded-full text-foreground/60 transition-all duration-300 ease-out hover:bg-foreground/5 hover:text-foreground ${className}`}
    >
      <SearchIcon className="h-4 w-4" strokeWidth={1.75} />
    </button>
  );
}
